import { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Timer, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SessionTimerProps {
  startTime: Date;
}

export function SessionTimer({ startTime }: SessionTimerProps) {
  const [elapsed, setElapsed] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const start = new Date(startTime).getTime();
      const diff = Math.floor((now - start) / 1000);
      setElapsed(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  const isWarning = minutes > 30;
  const isMinimum = minutes < 10;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        backgroundColor: isWarning ? 'warning.light' : 'background.paper',
        borderRadius: 2,
      }}
    >
      <Timer />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h5" component="div" fontWeight={600}>
          {formattedTime}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('sessionTimer.duration')}
        </Typography>
      </Box>
      {isWarning && (
        <Chip
          icon={<Warning />}
          label={t('sessionTimer.longSession')}
          color="warning"
          size="small"
        />
      )}
      {isMinimum && (
        <Chip label={t('sessionTimer.lessThan10')} color="info" size="small" variant="outlined" />
      )}
    </Box>
  );
}
